import { PIX_CONFIG } from "@/config";
import type {
  FinancialSummary,
  Module,
  Payment,
  PixInfo,
} from "../types/financial";
import api from "./api";

// PIX Info from centralized config
const defaultPixInfo: PixInfo = PIX_CONFIG;

// Transform backend response to frontend format
// Backend returns camelCase, so we handle both camelCase and snake_case for compatibility
const transformPayment = (backendPayment: any): Payment => ({
  id: String(backendPayment.id),
  month:
    backendPayment.monthNumber ||
    backendPayment.month_number ||
    backendPayment.month,
  dueDate: backendPayment.dueDate || backendPayment.due_date,
  paidDate: backendPayment.paidDate || backendPayment.paid_date,
  amount: parseFloat(backendPayment.amount),
  status: backendPayment.status,
  receiptUrl:
    backendPayment.receiptFileUrl ||
    backendPayment.receipt_file_url ||
    backendPayment.receiptUrl ||
    backendPayment.receipt_url,
});

const transformModule = (backendModule: any): Module => ({
  id: String(backendModule.id),
  name:
    backendModule.levelName ||
    backendModule.level_name ||
    backendModule.level?.name ||
    backendModule.name ||
    "Current Module",
  startDate: backendModule.startDate || backendModule.start_date,
  endDate: backendModule.endDate || backendModule.end_date,
  payments: backendModule.payments?.map(transformPayment) || [],
  totalAmount: parseFloat(
    backendModule.totalAmount || backendModule.total_amount || 0,
  ),
  paidAmount: parseFloat(
    backendModule.paidAmount || backendModule.paid_amount || 0,
  ),
});

// Service functions
export const financialService = {
  getFinancialSummary: async (): Promise<FinancialSummary> => {
    try {
      const response = await api.get("/financial/summary/");
      const data = response.data;

      // Handle both camelCase and snake_case from backend
      const moduleData = data.currentModule || data.current_module;
      const currentModule = moduleData ? transformModule(moduleData) : null;

      const pendingPayment = currentModule?.payments.find(
        (p) => p.status === "pending" || p.status === "overdue",
      );

      return {
        currentModule,
        pixInfo: defaultPixInfo,
        nextPaymentDate: pendingPayment?.dueDate,
        nextPaymentAmount: pendingPayment?.amount,
        classType: data.classType || data.class_type,
        classTypeLabel: data.classTypeLabel || data.class_type_label,
        monthlyPrice: parseFloat(data.monthlyPrice || data.monthly_price || 0),
        monthlyHours: data.monthlyHours || data.monthly_hours,
      };
    } catch (error) {
      throw error;
    }
  },

  getPayments: async (): Promise<Payment[]> => {
    try {
      const response = await api.get("/payments/");
      return response.data.results
        ? response.data.results.map(transformPayment)
        : response.data.map(transformPayment);
    } catch (error) {
      throw error;
    }
  },

  getCurrentModule: async (): Promise<Module | null> => {
    try {
      const response = await api.get("/modules/current/");
      return transformModule(response.data);
    } catch (error) {
      return null;
    }
  },

  getPixInfo: async (): Promise<PixInfo> => {
    // Currently using static PIX info - could be fetched from backend
    return defaultPixInfo;
  },

  uploadReceipt: async (paymentId: string, file: File): Promise<Payment> => {
    const formData = new FormData();
    formData.append("receipt", file);

    const response = await api.post(
      `/payments/${paymentId}/upload_receipt/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return transformPayment(response.data);
  },

  removeReceipt: async (paymentId: string): Promise<Payment> => {
    const response = await api.delete(`/payments/${paymentId}/remove_receipt/`);
    return transformPayment(response.data);
  },

  getModuleProgress: async (): Promise<{
    paid: number;
    total: number;
    percentage: number;
  }> => {
    try {
      const module = await financialService.getCurrentModule();

      if (!module) {
        return { paid: 0, total: 0, percentage: 0 };
      }

      const paidCount = module.payments.filter(
        (p) => p.status === "paid",
      ).length;
      const total = module.payments.length;

      return {
        paid: paidCount,
        total,
        percentage: total > 0 ? Math.round((paidCount / total) * 100) : 0,
      };
    } catch (error) {
      return { paid: 0, total: 0, percentage: 0 };
    }
  },
};

export default financialService;
